import { computed } from 'vue'
import { useStore } from 'vuex'
import type { Author, State } from '@/types'
import validateAuthorInfoView from '@/services/validation/authorInfo'

/**
 * Composable that manages author state and CRUD operations
 * for the submission workflow. It exposes reactive author lists,
 * the primary author, and utilities to add, update, delete,
 * and reset authors while orchestrating validation.
 *
 * This composable keeps Vuex mutations for shared multi-component
 * state management but centralises validation logic here.
 *
 * @returns {{
 *   authors: import('vue').ComputedRef<Author[]>;
 *   primaryAuthor: import('vue').ComputedRef<Author | undefined>;
 *   addAuthor: (author: Author) => Promise<void>;
 *   updateAuthor: (author: Author) => Promise<void>;
 *   deleteAuthor: (authorId: string) => Promise<void>;
 *   clearAllAuthors: () => Promise<void>;
 *   setAuthors: (authors: Author[]) => void;
 * }}
 * Object containing reactive state and helper functions for author management.
 */

export function useAuthorManagement() {
  const store = useStore<State>()

  const authors = computed<Author[]>({
    get: () => store.state.activeSubmission.authors,
    set: (val: Author[]) => store.commit('setAuthors', val),
  })

  const primaryAuthor = computed<Author | undefined>(() =>
    store.getters.primaryAuthor,
  )

  async function addAuthor(author: Author): Promise<void> {
    if (store.state.activeSubmission.authors.length === 0) {
      author.isCorresponding = true
    }

    store.commit('_addAuthor', author)
    await validateAuthorInfoView(store)
  }

  async function updateAuthor(author: Author): Promise<void> {
    store.commit('_updateAuthor', author)
    await validateAuthorInfoView(store)
  }

  async function deleteAuthor(authorId: string): Promise<void> {
    store.commit('_deleteAuthor', authorId)
    await validateAuthorInfoView(store)
  }

  async function clearAllAuthors(): Promise<void> {
    store.commit('_clearAllAuthors')
    await validateAuthorInfoView(store)
  }

  function setAuthors(newAuthors: Author[]): void {
    store.commit('setAuthors', newAuthors)
  }

  return {
    authors,
    primaryAuthor,
    addAuthor,
    updateAuthor,
    deleteAuthor,
    clearAllAuthors,
    setAuthors,
  }
}
